import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";

const UploadPanel = ({ open, onClose, onUpload }) => {
  const [figmaUrl, setFigmaUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!open) {
      setFigmaUrl("");
      setSelectedFile(null);
      setError("");
    }
  }, [open]);

  const handleUpload = () => {
    setError("");

    if (figmaUrl && isValidFigmaUrl(figmaUrl)) {
      onUpload({ figmaUrl, file: null });
    } else if (selectedFile) {
      onUpload({ figmaUrl: "", file: selectedFile });
    } else {
      setError("Please provide either a Figma URL or a file.");
      return;
    }

    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const isValidFigmaUrl = (url) =>
    /^https:\/\/www\.figma\.com\/design\/[a-zA-Z0-9]+\/.*$/.test(url);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "24px",
        },
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          fontFamily: "Manrope",
          fontSize: "18px",
          color: "#000",
          mb: 2,
        }}
      >
        Upload Panel
      </Typography>

      <DialogContent sx={{ padding: 0 }}>
        {/* Figma URL Input (Styled Like File Upload) */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <TextField
            fullWidth
            label="Figma URL"
            placeholder="Enter here"
            value={figmaUrl}
            disabled={!!selectedFile}
            onChange={(e) => setFigmaUrl(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              sx: {
                // border: "1px solid #c4c4c4",
                borderRadius: 1,
                backgroundColor: "#fff",
                height: "50px",
              },
            }}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "14px",
                fontFamily: "Manrope",
                padding: "12px",
              },
            }}
          />
        </Box>

        {/* OR Divider */}
        <Divider
          textAlign="center"
          sx={{
            my: 2,
            fontSize: "14px",
            fontWeight: 400,
            color: "#888",
          }}
        >
          OR
        </Divider>

        {/* File Upload Section */}
        <Box sx={{ position: "relative", mb: 3 }}>
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: "-10px",
              left: "12px",
              background: "#fff",
              px: "6px",
              fontSize: "13px",
              color: "#666",
              fontFamily: "Manrope",
            }}
          >
            File Upload
          </Typography>

          <Box
            sx={{
              border: "1px solid #c4c4c4",
              borderRadius: 1,
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                fontFamily: "Manrope",
                fontSize: "14px",
                color: "#666",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "70%",
              }}
            >
              {selectedFile?.name || "Choose here"}
            </Box>

            <Button
              variant="contained"
              component="label"
              startIcon={<AttachFileIcon />}
              disabled={!!figmaUrl} // 👈 disable if Figma URL is entered
              sx={{
                borderRadius: "24px",
                backgroundColor: "#2E5B9F",
                textTransform: "none",
              }}
            >
              Browse
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.docx,.pdf,.mp3,.mp4,.wav,.aac,.mkv,.m4a,.avi,.png,.jpg,.bmp"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Button>
          </Box>
        </Box>

        {/* Supported Formats Info */}
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Manrope",
            fontWeight: 600,
            fontSize: "12px",
            color: "#000",
            ml: 1,
          }}
        >
          Supported formats for upload:
          <Typography
            component="span"
            sx={{
              fontFamily: "Manrope",
              fontWeight: 500,
              fontSize: "12px",
              color: "#4B4B4B",
              ml: 0.5,
            }}
          >
            .docx, .pdf, .xls, .xlsx, .jpg, .png, .mp3, .mp4, .wav, .avi
          </Typography>
          <Typography
            component="span"
            sx={{
              fontFamily: "Manrope",
              fontWeight: 500,
              fontSize: "12px",
              color: "#B3261E",
              display: "block",
              mt: 0.5,
            }}
          >
          (For best results, please upload images with at least 800x600 px
            resolution.)
          </Typography>
        </Typography>
      </DialogContent>

      <Divider sx={{ my: 2, borderColor: "#E0E0E0" }} />

      {/* Action Buttons */}
      <DialogActions sx={{ justifyContent: "flex-end" }}>
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            fontFamily: "Manrope",
            fontWeight: 500,
            fontSize: "14px",
            color: "#49A3FF",
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!isValidFigmaUrl(figmaUrl) && !selectedFile}
          sx={{
            width: "95px",
            height: "40px",
            borderRadius: "4px",
            fontFamily: "Manrope",
            fontWeight: 500,
            fontSize: "14px",
            backgroundColor: "#49A3FF",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#49A3FF",
            },
            "&.Mui-disabled": {
              backgroundColor: "#B3D8FF",
              color: "#ffffff",
            },
          }}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadPanel;
